import { useState, useMemo } from 'react'
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  Skeleton,
  Tabs,
  Tab,
  List,
  ListItemButton,
  ListItemText,
  ToggleButtonGroup,
  ToggleButton,
  InputAdornment,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import PaymentIcon from '@mui/icons-material/Payment'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import SearchIcon from '@mui/icons-material/Search'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import type {
  RosterStudentDTO,
  AddTuitionInput,
  TuitionDTO,
  TermDTO,
  StudentDTO,
  AddEnrollmentInput,
  EnrollmentDTO,
} from '@school-homestead/shared'
import { SESSION_LABELS } from '@school-homestead/shared'
import type { Session, SessionOption } from '@school-homestead/shared'

const EMPTY_PAYMENT: AddTuitionInput = { studentId: 0, amount: 0, notes: '' }

export default function RosterPage() {
  const queryClient = useQueryClient()

  // ── Payment dialog state ──────────────────────────────────────────────────
  const [selectedStudent, setSelectedStudent] = useState<RosterStudentDTO | null>(null)
  const [paymentForm, setPaymentForm] = useState<AddTuitionInput>(EMPTY_PAYMENT)

  // ── Session tab ───────────────────────────────────────────────────────────
  const [sessionTab, setSessionTab] = useState<Session>('mon_wed')

  // ── Add-to-roster dialog state ────────────────────────────────────────────
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [pickedStudent, setPickedStudent] = useState<StudentDTO | null>(null)
  const [pickedSession, setPickedSession] = useState<SessionOption>('mon_wed')

  // ── Load active term ──────────────────────────────────────────────────────
  const { data: activeTerm } = useQuery({
    queryKey: ['terms', 'active'],
    queryFn: async () => {
      const res = await api.get<TermDTO | null>('/api/terms/active')
      if (!res.success) throw new Error(res.error)
      return res.data
    },
  })

  // ── Load roster for active term + selected session ────────────────────────
  const { data, isLoading, error } = useQuery({
    queryKey: ['roster', activeTerm?.id, sessionTab],
    queryFn: async () => {
      if (!activeTerm) return []
      const res = await api.get<RosterStudentDTO[]>(
        `/api/tuition?termId=${activeTerm.id}&session=${sessionTab}`,
      )
      if (!res.success) throw new Error(res.error)
      return res.data
    },
    enabled: !!activeTerm,
  })

  // ── Load all students for the search picker ───────────────────────────────
  const { data: allStudents, isLoading: studentsLoading } = useQuery({
    queryKey: ['students'],
    queryFn: async () => {
      const res = await api.get<StudentDTO[]>('/api/students')
      if (!res.success) throw new Error(res.error)
      return res.data
    },
    enabled: addDialogOpen,
  })

  // ── Filtered student list based on search query ───────────────────────────
  const filteredStudents = useMemo(() => {
    if (!allStudents) return []
    const q = searchQuery.trim().toLowerCase()
    if (!q) return allStudents
    return allStudents.filter(
      (s) =>
        s.childName.toLowerCase().includes(q) ||
        s.parentName.toLowerCase().includes(q),
    )
  }, [allStudents, searchQuery])

  // ── Payment mutation ──────────────────────────────────────────────────────
  const paymentMutation = useMutation({
    mutationFn: async (input: AddTuitionInput) => {
      const res = await api.post<TuitionDTO>('/api/tuition', input)
      if (!res.success) throw new Error(res.error)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roster'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      setSelectedStudent(null)
      setPaymentForm(EMPTY_PAYMENT)
    },
  })

  // ── Add-to-roster mutation ────────────────────────────────────────────────
  const addToRosterMutation = useMutation({
    mutationFn: async (input: AddEnrollmentInput) => {
      const res = await api.post<EnrollmentDTO>('/api/enrollments', input)
      if (!res.success) throw new Error(res.error)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roster'] })
      handleAddDialogClose()
    },
  })

  // ── Handlers ──────────────────────────────────────────────────────────────
  function openPayment(student: RosterStudentDTO) {
    setSelectedStudent(student)
    setPaymentForm({ studentId: student.id, amount: 0, notes: '' })
  }

  function handleClose() {
    setSelectedStudent(null)
    setPaymentForm(EMPTY_PAYMENT)
    paymentMutation.reset()
  }

  function handleAddDialogOpen() {
    setAddDialogOpen(true)
    setSearchQuery('')
    setPickedStudent(null)
    setPickedSession('mon_wed')
    addToRosterMutation.reset()
  }

  function handleAddDialogClose() {
    setAddDialogOpen(false)
    setSearchQuery('')
    setPickedStudent(null)
    setPickedSession('mon_wed')
    addToRosterMutation.reset()
  }

  function handleAddToRoster() {
    if (!pickedStudent || !activeTerm) return
    addToRosterMutation.mutate({
      studentId: pickedStudent.id,
      termId: activeTerm.id,
      session: pickedSession,
      status: 'enrolled',
    })
  }

  // ── Derived counts ────────────────────────────────────────────────────────
  const paidCount = (data ?? []).filter((s) => s.hasPaidThisMonth).length
  const unpaidCount = (data ?? []).filter((s) => !s.hasPaidThisMonth).length

  const students = data ?? []

  if (!activeTerm) {
    return (
      <Box>
        <Typography variant="h5" fontWeight={700} mb={3}>
          Tuition
        </Typography>
        <Alert severity="info">
          No active term set. Please contact an administrator to activate a term.
        </Alert>
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h5" fontWeight={700}>
          Tuition{' '}
          {!isLoading && students.length > 0 && (
            <Typography component="span" variant="body2" color="text.secondary" fontWeight={400}>
              ({students.length})
            </Typography>
          )}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip label={activeTerm.name} color="primary" size="small" />
          <Button
            variant="contained"
            size="small"
            startIcon={<PersonAddIcon />}
            onClick={handleAddDialogOpen}
          >
            Add Student
          </Button>
        </Box>
      </Box>

      {/* Session Tabs */}
      <Tabs
        value={sessionTab}
        onChange={(_, v) => setSessionTab(v)}
        sx={{ mb: 2 }}
      >
        <Tab label={SESSION_LABELS.mon_wed} value="mon_wed" />
        <Tab label={SESSION_LABELS.tue_thu} value="tue_thu" />
      </Tabs>

      {/* Summary chips */}
      {!isLoading && data && (
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Chip
            icon={<CheckCircleIcon />}
            label={`${paidCount} paid`}
            color="success"
            variant="outlined"
            size="small"
          />
          <Chip
            icon={<RadioButtonUncheckedIcon />}
            label={`${unpaidCount} unpaid`}
            color="warning"
            variant="outlined"
            size="small"
          />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load roster.
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Parent</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Last Payment</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Notes</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading
              ? [1, 2, 3, 4, 5].map((i) => (
                  <TableRow key={i}>
                    {[1, 2, 3, 4, 5, 6].map((j) => (
                      <TableCell key={j}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : students.map((student) => (
                  <TableRow key={student.enrollmentId} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {student.childName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{student.parentName}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={student.hasPaidThisMonth ? 'Paid' : 'Unpaid'}
                        size="small"
                        color={student.hasPaidThisMonth ? 'success' : 'warning'}
                        variant="filled"
                      />
                    </TableCell>
                    <TableCell>
                      {student.lastPayment ? (
                        <Typography variant="body2">
                          <strong>${student.lastPayment.amount.toFixed(2)}</strong>{' '}
                          on {new Date(student.lastPayment.datePaid).toLocaleDateString()}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No payments
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {student.lastPayment?.notes || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<PaymentIcon />}
                        onClick={() => openPayment(student)}
                      >
                        Record
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}

            {!isLoading && students.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    No enrolled students for {SESSION_LABELS[sessionTab]} in {activeTerm.name}.
                    Use the <strong>Add Student</strong> button to enroll a student.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ── Record Payment Dialog ─────────────────────────────────────────── */}
      <Dialog open={!!selectedStudent} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle fontWeight={700}>Record Tuition Payment</DialogTitle>
        <DialogContent sx={{ pt: '20px !important', display: 'flex', flexDirection: 'column', gap: 2 }}>
          {selectedStudent && (
            <Typography variant="body1" color="text.secondary">
              Student: <strong>{selectedStudent.childName}</strong>
            </Typography>
          )}
          <TextField
            label="Amount ($)"
            type="number"
            value={paymentForm.amount > 0 ? paymentForm.amount : ''}
            onChange={(e) =>
              setPaymentForm({ ...paymentForm, amount: parseFloat(e.target.value) || 0 })
            }
            inputProps={{ min: 0, step: '0.01' }}
            fullWidth
            autoFocus
          />
          <TextField
            label="Notes (optional)"
            value={paymentForm.notes ?? ''}
            onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
            fullWidth
            multiline
            rows={2}
          />

          {paymentMutation.isError && (
            <Alert severity="error">
              {(paymentMutation.error as Error).message}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleClose} disabled={paymentMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => paymentMutation.mutate(paymentForm)}
            disabled={paymentForm.amount <= 0 || paymentMutation.isPending}
          >
            Save Payment
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Add Student to Roster Dialog ──────────────────────────────────── */}
      <Dialog open={addDialogOpen} onClose={handleAddDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle fontWeight={700}>Add Student to Roster</DialogTitle>
        <DialogContent
          sx={{ pt: '20px !important', display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          {/* Step 1 — search */}
          <TextField
            label="Search by student or parent name"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value)
              setPickedStudent(null)
            }}
            fullWidth
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: studentsLoading ? (
                <InputAdornment position="end">
                  <CircularProgress size={16} />
                </InputAdornment>
              ) : null,
            }}
          />

          {/* Student list */}
          {!pickedStudent && (
            <Box
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                maxHeight: 240,
                overflowY: 'auto',
              }}
            >
              {filteredStudents.length === 0 && !studentsLoading ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                  {searchQuery ? 'No students match your search.' : 'Start typing to search students.'}
                </Typography>
              ) : (
                <List dense disablePadding>
                  {filteredStudents.map((s) => (
                    <ListItemButton
                      key={s.id}
                      onClick={() => setPickedStudent(s)}
                      divider
                    >
                      <ListItemText
                        primary={s.childName}
                        secondary={`Parent: ${s.parentName}`}
                      />
                    </ListItemButton>
                  ))}
                </List>
              )}
            </Box>
          )}

          {/* Selected student confirmation */}
          {pickedStudent && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 1.5,
                border: 1,
                borderColor: 'primary.main',
                borderRadius: 1,
                bgcolor: 'primary.50',
              }}
            >
              <Box>
                <Typography variant="subtitle2" fontWeight={700}>
                  {pickedStudent.childName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Parent: {pickedStudent.parentName}
                </Typography>
              </Box>
              <Button
                size="small"
                onClick={() => {
                  setPickedStudent(null)
                  setSearchQuery('')
                }}
              >
                Change
              </Button>
            </Box>
          )}

          {/* Step 2 — session picker (always visible so user can pre-select) */}
          <Box>
            <Typography variant="body2" fontWeight={600} mb={1}>
              Session
            </Typography>
            <ToggleButtonGroup
              value={pickedSession}
              exclusive
              onChange={(_, v) => { if (v) setPickedSession(v) }}
              size="small"
              fullWidth
            >
              <ToggleButton value="mon_wed">
                {SESSION_LABELS.mon_wed}
              </ToggleButton>
              <ToggleButton value="tue_thu">
                {SESSION_LABELS.tue_thu}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {addToRosterMutation.isError && (
            <Alert severity="error">
              {(addToRosterMutation.error as Error).message}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleAddDialogClose} disabled={addToRosterMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={handleAddToRoster}
            disabled={!pickedStudent || addToRosterMutation.isPending}
          >
            {addToRosterMutation.isPending ? 'Adding…' : 'Add to Roster'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
